// Content utilities for handling both HTML and plain text content

/**
 * Checks if a string contains HTML tags
 * @param {string} str - The string to check
 * @returns {boolean} - True if HTML is detected
 */
export const isHtml = (str) => {
  if (!str || typeof str !== 'string') return false;
  
  // Check for common HTML patterns
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(str);
};

/**
 * Converts plain text to HTML paragraphs
 * @param {string} text - Plain text to convert
 * @returns {string} - HTML formatted text
 */
export const plainTextToHtml = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // If it's already HTML, return as is
  if (isHtml(text)) return text;
  
  // Convert plain text to HTML
  return text
    .split('\n\n') // Split double newlines into paragraphs
    .filter(paragraph => paragraph.trim()) // Remove empty paragraphs
    .map(paragraph => {
      // Handle single line breaks within paragraphs
      const processedParagraph = paragraph
        .split('\n')
        .map(line => line.trim())
        .filter(line => line)
        .join('<br>');
      
      return `<p>${processedParagraph}</p>`;
    })
    .join('');
};

/**
 * Strips HTML tags and returns plain text
 * @param {string} html - HTML content
 * @returns {string} - Plain text content
 */
export const htmlToPlainText = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  // Create a temporary DOM element to strip HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

/**
 * Safely gets content for editing, converting plain text to HTML if needed
 * @param {string} content - The content (HTML or plain text)
 * @returns {string} - HTML content ready for rich text editor
 */
export const getEditableContent = (content) => {
  if (!content) return '';
  
  // If it's already HTML, return as is
  if (isHtml(content)) {
    return content;
  }
  
  // Convert plain text to HTML - wrap in paragraph for legacy content
  return `<p>${content.replace(/\n/g, '<br>')}</p>`;
};

/**
 * Safely gets content for display, handling both HTML and plain text
 * @param {string} content - The content (HTML or plain text)
 * @returns {string} - Content ready for display
 */
export const getDisplayContent = (content) => {
  if (!content) return '';
  
  // If it's HTML, return as is
  if (isHtml(content)) {
    return content;
  }
  
  // If it's plain text, wrap in paragraph tags for proper display
  return `<p>${content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
};

/**
 * Enhances plain text content with basic formatting detection
 * @param {string} text - Plain text content
 * @returns {string} - Enhanced HTML content
 */
export const enhancePlainText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // If it's already HTML, return as is
  if (isHtml(text)) return text;
  
  let enhanced = text;
  
  // Convert bullet points (lines starting with - or * or •)
  enhanced = enhanced.replace(/^[\s]*[-*•]\s+(.+)$/gm, '<li>$1</li>');
  
  // Wrap consecutive list items in ul tags
  enhanced = enhanced.replace(/((<li>.*<\/li>\s*)+)/g, '<ul>$1</ul>');
  
  // Convert numbered lists (1. 2. etc.)
  enhanced = enhanced.replace(/^[\s]*\d+\.\s+(.+)$/gm, '<li>$1</li>');
  
  // Wrap consecutive numbered list items in ol tags
  enhanced = enhanced.replace(/((<li>.*<\/li>\s*)+)/g, (match) => {
    // Check if this is after a numbered pattern
    const beforeMatch = enhanced.substring(0, enhanced.indexOf(match));
    if (/\d+\.\s+/.test(beforeMatch.split('\n').pop())) {
      return `<ol>${match}</ol>`;
    }
    return match;
  });
  
  // Convert headers (lines starting with # or ## etc.)
  enhanced = enhanced.replace(/^#+\s+(.+)$/gm, (match, title) => {
    const level = match.indexOf(' ');
    return `<h${Math.min(level, 6)}>${title}</h${Math.min(level, 6)}>`;
  });
  
  // Convert paragraphs
  enhanced = plainTextToHtml(enhanced);
  
  return enhanced;
};

/**
 * Validates and cleans HTML content
 * @param {string} html - HTML content to validate
 * @returns {string} - Cleaned HTML content
 */
export const validateHtmlContent = (html) => {
  if (!html) return '';
  
  // Basic validation - ensure we have proper paragraph tags
  if (isHtml(html)) {
    // If it's HTML but doesn't have paragraph tags, wrap in paragraph
    if (!html.includes('<p>') && !html.includes('<h') && !html.includes('<ul>') && !html.includes('<ol>')) {
      return `<p>${html}</p>`;
    }
    return html;
  }
  
  // Convert plain text to HTML
  return plainTextToHtml(html);
};

/**
 * Gets a preview of content (first few words)
 * @param {string} content - HTML or plain text content
 * @param {number} wordLimit - Maximum number of words
 * @returns {string} - Preview text
 */
export const getContentPreview = (content, wordLimit = 20) => {
  if (!content) return '';
  
  const plainText = isHtml(content) ? htmlToPlainText(content) : content;
  const words = plainText.split(/\s+/).filter(word => word.trim());
  
  if (words.length <= wordLimit) {
    return plainText;
  }
  
  return words.slice(0, wordLimit).join(' ') + '...';
};

/**
 * Converts HTML to styled text with basic formatting for React Native
 * @param {string} content - HTML or plain text content
 * @returns {string} - Formatted text with basic styling indicators
 */
export const getStyledTextForNative = (content) => {
  if (!content) return '';
  
  // If it's plain text, return as is
  if (!isHtml(content)) {
    return content;
  }
  
  let text = content;
  
  // Convert headers to uppercase with spacing
  text = text.replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, (match, content) => {
    return `\n\n${content.toUpperCase()}\n`;
  });
  
  // Convert bold tags to asterisks
  text = text.replace(/<(strong|b)[^>]*>(.*?)<\/(strong|b)>/gi, '*$2*');
  
  // Convert italic tags to underscores
  text = text.replace(/<(em|i)[^>]*>(.*?)<\/(em|i)>/gi, '_$2_');
  
  // Convert list items with bullet points
  text = text.replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n');
  
  // Remove ul/ol tags but keep content
  text = text.replace(/<\/?[uo]l[^>]*>/gi, '\n');
  
  // Convert blockquotes with quotation marks
  text = text.replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gi, '"$1"\n');
  
  // Convert paragraphs to double line breaks
  text = text.replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n');
  
  // Convert line breaks
  text = text.replace(/<br\s*\/?>/gi, '\n');
  
  // Remove any remaining HTML tags
  text = text.replace(/<[^>]*>/g, '');
  
  // Clean up multiple newlines and decode HTML entities
  text = text
    .replace(/\n{3,}/g, '\n\n')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
  
  return text;
};

/**
 * Gets formatted content for web display (preserves HTML)
 * @param {string} content - HTML or plain text content
 * @returns {string} - HTML content ready for web display
 */
export const getWebDisplayContent = (content) => {
  if (!content) return '';
  
  // If it's already HTML, return as is
  if (isHtml(content)) {
    return content;
  }
  
  // Convert plain text to HTML for web display with proper paragraph tags
  return `<p>${content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
};

const contentUtils = {
  isHtml,
  plainTextToHtml,
  htmlToPlainText,
  getEditableContent,
  getDisplayContent,
  enhancePlainText,
  validateHtmlContent,
  getContentPreview,
  getStyledTextForNative,
  getWebDisplayContent
};

export default contentUtils;